/**
 * SEO head helper for TanStack Start.
 *
 * Returns a head() config object: { meta, links, scripts }.
 * Title is placed inside meta (top-level title is silently ignored).
 * Canonical is on the LEAF only (root would duplicate — TanStack links array
 * does not dedupe).
 */

export interface SeoOptions {
  /** Full page title. Will be set verbatim — include " · Currency Thoughts" yourself if you want it. */
  title: string;
  /** ≤160 chars for best Google snippet rendering. */
  description: string;
  /** Absolute or relative URL of the og/twitter image. Omit if you don't have a useful one. */
  ogImage?: string;
  /** og:type — defaults to "website". Use "article" for lessons, "product" for store. */
  ogType?: "website" | "article" | "product" | "profile";
  /** Relative path (e.g. "/about"). Becomes both <link rel="canonical"> and og:url. */
  canonical?: string;
  /** Twitter card style. Defaults to "summary_large_image". */
  twitterCard?: "summary" | "summary_large_image";
  /** Block crawlers from indexing this route. */
  noindex?: boolean;
  /** One or more JSON-LD objects to emit as <script type="application/ld+json">. */
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

type MetaEntry =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

type LinkEntry = { rel: string; href: string };

type ScriptEntry = { type: string; children: string };

const SITE_URL = "https://currencythoughts.net";

function absolutize(path: string) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function seo(opts: SeoOptions): {
  meta: MetaEntry[];
  links: LinkEntry[];
  scripts: ScriptEntry[];
} {
  const {
    title,
    description,
    ogImage,
    ogType = "website",
    canonical,
    twitterCard = "summary_large_image",
    noindex = false,
    schema,
  } = opts;

  const meta: MetaEntry[] = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: ogType },
    { name: "twitter:card", content: ogImage ? twitterCard : "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];

  if (noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }
  const canonicalAbs = canonical ? absolutize(canonical) : undefined;
  if (canonicalAbs) {
    meta.push({ property: "og:url", content: canonicalAbs });
  }
  if (ogImage) {
    const ogImageAbs = absolutize(ogImage);
    meta.push({ property: "og:image", content: ogImageAbs });
    meta.push({ name: "twitter:image", content: ogImageAbs });
  }

  const links: LinkEntry[] = [];
  if (canonicalAbs) {
    links.push({ rel: "canonical", href: canonicalAbs });
  }

  const scripts: ScriptEntry[] = [];
  if (schema) {
    const arr = Array.isArray(schema) ? schema : [schema];
    for (const s of arr) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(s),
      });
    }
  }

  return { meta, links, scripts };
}